import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-progress-bar>` says about itself.
 *
 * It has THREE states, not two, and the third is the one usually left out of a component library.
 *
 *  1. Determinate — a `value` between `min` and `max` fills the track, and `aria-valuenow` carries
 *     the same number the fill draws.
 *  2. Indeterminate — `indeterminate` is set, a segment slides along the track, and `aria-valuenow`
 *     is REMOVED rather than pinned at zero, because "I do not know how far" and "nought per cent"
 *     are different statements and only one of them is true.
 *  3. Indeterminate under `prefers-reduced-motion: reduce` — the slide is turned off and the track
 *     is drawn as a static stripe instead. Not nothing: an animation removed and not replaced would
 *     leave a reduced-motion reader with an empty grey track, which reads as "stalled". The stripe
 *     still reads as "in progress" without moving.
 *
 * The third has its own example below, and that example is deliberately the SAME placement as the
 * second. An example is data — a placement cannot switch on a media query, and faking the fallback
 * with injected styling would be documenting a thing the component does not do. So the two are
 * declared as what they are: one element, drawn one way or the other by the reader's own system
 * setting. Turn reduced motion on and the second example becomes the third.
 *
 * The `intent` colours are worth one note: the default fill reads the brand accent and MOVES when a
 * tenant repaints it, while success, warning and danger read the semantic tokens and stay put.
 */
const docs: ComponentDocs = {
  aliases: ['loading bar', 'progress indicator', 'percent bar', 'meter', 'determinate spinner'],

  answers: [
    'show how far a long job has got',
    'show that something is running when you cannot say how far',
    'show an import or a parse working through a file',
    'show how much of a quota or a budget is used',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'value',
      attribute: 'value',
      type: 'number',
      description:
        'How far along, on the min–max scale. It is clamped to that range, and a value that will not ' +
        'parse falls back to `min`. Ignored entirely while `indeterminate` is set.',
      default: 0,
    },
    {
      name: 'min',
      attribute: 'min',
      type: 'number',
      description: 'The bottom of the scale — an empty track.',
      default: 0,
    },
    {
      name: 'max',
      attribute: 'max',
      type: 'number',
      description:
        'The top of the scale — a full track. Set it to the real total (files, rows, dollars) and pass ' +
        'the raw count as the value rather than converting to a percentage first.',
      default: 100,
    },
    {
      name: 'indeterminate',
      attribute: 'indeterminate',
      type: 'boolean',
      description:
        'This is running and there is no honest number to give. Slides a segment along the track, ' +
        'drops `aria-valuenow`, and blanks the percentage readout.',
      default: false,
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The visible caption above the track, which also becomes the bar\'s accessible name. Unset, ' +
        'the caption line is not drawn at all.',
    },
    {
      name: 'showValue',
      attribute: 'show-value',
      type: 'boolean',
      description:
        'Print the percentage at the right-hand end of the caption line, rounded to a whole number. It ' +
        'is always a PERCENTAGE of the min–max span, never the raw value, so a bar counting 0–2400 ' +
        'rows still reads "62%".',
      default: false,
    },
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['accent', 'success', 'warning', 'danger'],
      description:
        'The fill colour: accent, success, warning or danger. The accent moves with a tenant\'s brand; ' +
        'the three semantic ones do not.',
      default: 'accent',
    },
    {
      name: 'variant',
      attribute: 'variant',
      type: 'text',
      options: ['bar', 'circular'],
      description:
        'The SHAPE the same measurement takes: a horizontal bar, or a ring. Everything else is ' +
        'unchanged by it — the value, the bounds, the intent colours, the indeterminate state and ' +
        'its reduced-motion fallback, and what a screen reader is told. A ring is not a different ' +
        'component measuring the same thing; it is this one drawn round. With show-value, the ' +
        'percentage moves into the middle of the ring, which is the only place it reads as belonging ' +
        'to it.',
      default: 'bar',
    },
  ],

  // Read from the source: the bar reports nothing. It is told how far along it is and draws that;
  // whatever is running raises its own events.
  events: [],

  // Nothing is slotted — the caption comes in through `label` and the readout is generated.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-surface-2', controls: 'The unfilled track, and the unfilled part of the ring.' },
    { token: '--caos-color-accent', controls: 'The default fill, and the stripes of the reduced-motion fallback.' },
    { token: '--caos-color-success', controls: 'The fill when intent is success.' },
    { token: '--caos-color-warning', controls: 'The fill when intent is warning.' },
    { token: '--caos-color-danger', controls: 'The fill when intent is danger.' },
    { token: '--caos-radius-pill', controls: 'The rounded ends of the track and the fill.' },
    { token: '--caos-color-text', controls: 'The caption.' },
    { token: '--caos-color-text-muted', controls: 'The percentage readout.' },
    { token: '--caos-font', controls: 'The typeface of the caption and the readout.' },
    { token: '--caos-space-3', controls: 'The gap between the caption and the readout.' },
    { token: '--caos-space-2', controls: 'The gap between the caption line and the track.' },
  ],

  parts: [
    { part: 'bar', is: 'the whole block, header and track' },
    { part: 'header', is: 'the label-and-value line above the track' },
    { part: 'label', is: 'the label text' },
    { part: 'value', is: 'the value text' },
    { part: 'track', is: 'the groove' },
    { part: 'fill', is: 'the filled part of the groove' },
    {
      part: 'ring',
      is: 'the circle the progress is drawn on',
      when: 'Always present as a part; it is only drawn while the variant is circular.',
    },
  ],

  methods: [],

  placing:
    'It is a block and fills the width it is given, so it takes its width from the region rather than ' +
    'from itself — put it in the column that belongs to whatever is running. The caption line is part ' +
    'of the piece, so nothing above it needs a heading of its own. The track is 8px and does not ' +
    'change with the size scale. The circular variant is square and takes the width it is given too, ' +
    'up to the top step of the size scale so it cannot grow to the width of a page; it carries no ' +
    'size attribute of its own, because the scale exists for the box an icon draws in, the diameter ' +
    'of an avatar and the height of a control, and a progress ring is none of those three.',

  whenToUse:
    'One operation that takes long enough for somebody to wonder whether it is still going. Use the ' +
    'determinate form whenever a real fraction is available and the indeterminate form when it is not ' +
    '— inventing a number to avoid the indeterminate state is how a bar ends up sitting at 90%.',

  insteadUse: [
    {
      name: 'SkeletonBlock',
      when:
        'A surface is LOADING and there is no operation to report on. The skeleton says "content is ' +
        'coming and this is the shape of it"; a bar says "a job is running and here is how far".',
    },
    {
      name: 'Button',
      when:
        'The wait belongs to the control that started it and is short. The button\'s own `loading` flag ' +
        'draws a spinner and marks itself busy, which is the whole story for a save.',
    },
    {
      name: 'Toast',
      when: 'The operation has FINISHED and the outcome is what wants saying.',
    },
    {
      name: 'StatTile',
      when:
        'The number is a figure to read rather than progress towards a finish. A bar implies something ' +
        'is moving and will end.',
    },
  ],

  accessibility:
    'The element is a `role="progressbar"` and keeps `aria-valuemin`, `aria-valuemax` and ' +
    '`aria-valuenow` in step with what is drawn, so the percentage on screen and the percentage ' +
    'announced cannot disagree; `label` is mirrored to the accessible name. The indeterminate state ' +
    'REMOVES `aria-valuenow` rather than reporting zero, and its animation is replaced — not merely ' +
    'stopped — under `prefers-reduced-motion: reduce`, so nobody is left looking at a track that seems ' +
    'stalled. Left to the author: giving it a `label` (a bar with no name is announced as a bare ' +
    'percentage), and not relying on the fill colour alone to say that something went wrong.',

  examples: [
    {
      key: 'determinate',
      label: 'A job with a number',
      summary:
        'The ordinary case: a caption, a fill at 62% of the scale, and the readout — the number drawn ' +
        'and the number announced are the same one.',
      placement: {
        component: 'caos-progress-bar',
        inputs: { value: 62, label: 'Importing records', showValue: true },
      },
    },
    {
      key: 'indeterminate',
      label: 'A job with no number',
      summary:
        'Running with nothing honest to report: a segment slides along the track, the readout is blank, ' +
        'and `aria-valuenow` is dropped rather than reported as zero.',
      placement: {
        component: 'caos-progress-bar',
        inputs: { indeterminate: true, label: 'Waiting for the report to build' },
      },
    },
    {
      key: 'reduced-motion',
      label: 'The same job, reduced motion',
      summary:
        'The same element under `prefers-reduced-motion: reduce` — the slide is replaced by a static ' +
        'stripe rather than simply removed, so it still reads as running. Turn the setting on and this ' +
        'is what the example above becomes.',
      placement: {
        component: 'caos-progress-bar',
        inputs: { indeterminate: true, label: 'Waiting for the report to build' },
      },
    },
    {
      key: 'intents',
      label: 'The four fills',
      summary:
        'Every fill colour on the same value, so a bar used as a gauge — a quota nearly spent, a budget ' +
        'overrun — can be coloured by what the number means.',
      placement: [
        { component: 'caos-progress-bar', inputs: { value: 62, label: 'Default (brand accent)', showValue: true } },
        { component: 'caos-progress-bar', inputs: { value: 62, intent: 'success', label: 'Success', showValue: true } },
        { component: 'caos-progress-bar', inputs: { value: 62, intent: 'warning', label: 'Warning', showValue: true } },
        { component: 'caos-progress-bar', inputs: { value: 62, intent: 'danger', label: 'Danger', showValue: true } },
      ],
    },
  ],
};

export default docs;
